import { Permit, PermitType, Santri, WhatsappLog } from '../types';
import { IzinStafRecord } from '../hooks/useIzinStaf';
import { db } from '../firebase';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { getJenjangFromClass } from './jenjangUtils';
import { sendWhatsapp, WaDevice } from './proxy';

// --- KONFIGURASI ---
// Device ID WhaCenter disimpan di server (api/proxy), bukan di browser.
const WHACENTER_DEVICE_ID: WaDevice = 'putra';
const WHACENTER_DEVICE_ID_PUTRI: WaDevice = 'putri';
// Nama grup tidak rahasia, tetap dari env VITE_.
const GROUP_NAME_SMP = import.meta.env.VITE_GROUP_NAME_SMP;
const GROUP_NAME_SMA = import.meta.env.VITE_GROUP_NAME_SMA;
const GROUP_NAME_PUTRI_SMP = import.meta.env.VITE_GROUP_PUTRI_SMP;
const GROUP_NAME_PUTRI_SMA = import.meta.env.VITE_GROUP_PUTRI_SMA;

// --- Logging ---

/**
 * Mencatat upaya pengiriman pesan ke Firestore.
 * Mengembalikan ID dari dokumen log yang baru dibuat.
 */
const createWhatsappLog = async (logData: Omit<WhatsappLog, 'id' | 'status' | 'timestamp'>): Promise<string> => {
    const docRef = await addDoc(collection(db, 'whatsapp_logs'), {
        ...logData,
        status: 'pending',
        timestamp: new Date(),
    });
    return docRef.id;
};

/**
 * Memperbarui status log setelah upaya pengiriman.
 */
const updateWhatsappLog = async (logId: string, status: 'sent' | 'failed', error?: string) => {
    const logRef = doc(db, 'whatsapp_logs', logId);
    const updateData: { status: 'sent' | 'failed', error?: string } = { status };
    if (error) {
        updateData.error = error;
    }
    await updateDoc(logRef, updateData);
};


// --- Helper Functions ---

/**
 * Format waktu menjadi string yang mudah dibaca.
 */
const formatDateOnly = (dateStr: string): string => {
    try {
        const date = new Date(dateStr);
        return new Intl.DateTimeFormat('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }).format(date);
    } catch {
        return dateStr;
    }
};

const formatTime = (date: Date): string => {
    return new Intl.DateTimeFormat('id-ID', {
        hour: '2-digit',
        minute: '2-digit',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    }).format(date);
};

/**
 * Fungsi dasar untuk mengirim pesan lewat WhaCenter (via api/proxy) dan mencatat hasilnya.
 */
const sendRequest = async (target: { number?: string; group?: string }, message: string, logData: Omit<WhatsappLog, 'id' | 'status' | 'timestamp'>, deviceId: WaDevice = WHACENTER_DEVICE_ID): Promise<void> => {
    const logId = await createWhatsappLog(logData);

    try {
        const response = await sendWhatsapp(target, message, deviceId);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMsg = errorData.info || 'Gagal mengirim pesan.';
            console.error('Gagal mengirim pesan WhatsApp via Whacenter:', errorMsg);
            await updateWhatsappLog(logId, 'failed', errorMsg);
        } else {
            console.log('Request pengiriman pesan WhatsApp berhasil dikirim. Tujuan:', target);
            await updateWhatsappLog(logId, 'sent');
        }
    } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        console.error('Terjadi error saat menghubungi API Whacenter:', errorMsg);
        await updateWhatsappLog(logId, 'failed', errorMsg);
    }
};

/**
 * Mengirim pesan ke nomor pribadi.
 */
const sendToNumber = async (phoneNumber: string, message: string, permit: Permit, deviceId: WaDevice = WHACENTER_DEVICE_ID) => {
    const logData = {
        permitId: permit.id,
        santriName: permit.santri[0]?.nama || 'N/A',
        recipient: phoneNumber,
        message: message,
    };
    await sendRequest({ number: phoneNumber }, message, logData, deviceId);
};

/**
 * Mengirim pesan ke grup.
 */
const sendToGroup = async (groupName: string, message: string, permit: Permit, deviceId: WaDevice = WHACENTER_DEVICE_ID) => {
    if (!groupName) {
        console.log("Pesan Grup TIDAK DIKIRIM: Nama grup tidak di-set.");
        // Optional: Log this failure as well
        return;
    }
    const logData = {
        permitId: permit.id,
        santriName: permit.pembimbing || permit.santri[0]?.nama || 'N/A',
        recipient: groupName,
        message: message,
    };
    await sendRequest({ group: groupName }, message, logData, deviceId);
};


// --- Message Formatting ---

const formatIzinPulangCheckout = (permit: Permit): string => {
    const santri = permit.santri[0];
    return `🔈🔈 *Dengan ini kami informasikan izin pulang santri:*
- *Nama Santri :* ${santri.nama}
- *Kelas :* ${santri.kelas}
- *Asrama :* ${santri.asrama}
- *Keperluan :* ${permit.alasan}
- *Tujuan :* ${permit.tujuan}
- *Tanggal & Jam izin :* ${formatTime(permit.checkoutTime!)}
- *Tanggal & Jam Kembali :* ${formatTime(permit.rencanaKembali)}`;
};

const formatIzinPulangCheckin = (permit: Permit): string => {
    const santri = permit.santri[0];
    const keterangan = permit.keteranganTelat || 'Tepat Waktu'; // Default

    return `✅✅ *Informasi Kehadiran.*
Alhamdulillah, telah kembali ke ma'had santri atas nama:
- *Nama Santri :* ${santri.nama}
- *Kelas :* ${santri.kelas}
- *Asrama :* ${santri.asrama}
- *Waktu Kedatangan :* ${formatTime(permit.checkinTime!)}
- *Keterangan :* ${keterangan}`;
};

const formatIzinTerbimbing = (permit: Permit, event: 'checkout' | 'checkin'): string => {
    const MAX_DISPLAY = 5;
    const totalSantri = permit.santri.length;

    let santriList = permit.santri
        .slice(0, MAX_DISPLAY)
        .map(s => `- ${s.nama} (${s.kelas})`)
        .join('\n');

    if (totalSantri > MAX_DISPLAY) {
        const remaining = totalSantri - MAX_DISPLAY;
        santriList += `\n... dan ${remaining} santri lainnya.`;
    }

    const detailLink = `\n\nUntuk info lebih detail dan daftar santri lengkap, klik link berikut:\nhttps://portal.miastasikmalaya.com/#/perizinan/detail/${permit.id}`;

    if (event === 'checkout') {
        return `*Kami Informasikan Izin Terbimbing:*
- *Nama Pembimbing :* ${permit.pembimbing}
- *Keperluan :* ${permit.alasan}
- *Tujuan :* ${permit.tujuan}
- *Tanggal & Jam Izin :* ${formatTime(permit.checkoutTime!)}
- *Jam Kembali :* ${formatTime(permit.rencanaKembali)}
- *Jumlah santri :* ${totalSantri}
*Daftar Santri :*
${santriList}${totalSantri > MAX_DISPLAY ? detailLink : ''}`;
    } else { // checkin
        const keterangan = permit.keteranganTelat || 'Tepat Waktu';
        return `*Informasi Kehadiran.*
Alhamdulillah, rombongan Izin Terbimbing telah kembali:
- *Nama Pembimbing :* ${permit.pembimbing}
- *Waktu Kedatangan :* ${formatTime(permit.checkinTime!)}
- *Keterangan :* ${keterangan}
- *Jumlah santri :* ${totalSantri}
*Daftar Santri :*
${santriList}${totalSantri > MAX_DISPLAY ? detailLink : ''}`;
    }
};


// --- Klasifikasi Kelas ---

// --- Handler Notifikasi (Fungsi yang akan dipanggil dari Context) ---

export const resendMessageFromLog = async (log: WhatsappLog) => {
    // Perbarui log menjadi pending
    const logRef = doc(db, 'whatsapp_logs', log.id);
    await updateDoc(logRef, { status: 'pending', timestamp: new Date() });

    // Tentukan apakah ini grup atau nomor pribadi
    const isGroup =
        log.recipient === GROUP_NAME_SMP ||
        log.recipient === GROUP_NAME_SMA ||
        log.recipient === GROUP_NAME_PUTRI_SMP ||
        log.recipient === GROUP_NAME_PUTRI_SMA;

    const isPutriGroup = log.recipient === GROUP_NAME_PUTRI_SMP || log.recipient === GROUP_NAME_PUTRI_SMA;

    // Fallback: Jika recipient bukan grup, kita tidak tahu pasti apakah ini putri atau putra
    // hanya dari tabel log (kecuali kita simpan device_id di log).
    // Asumsi sementara: kalau dia bukan grup dan kita mau resend, kita akan coba pakai device ID putra
    // KECUALI kita tambahkan informasi ke log (yg mana membutuhkan migrasi).
    // Untuk saat ini, kita akan coba menebak dari `message` (apakah ada kata-kata khusus?)
    // atau gunakan device default.
    // Idealnya, device_id disimpan di WhatsappLog.
    const deviceId: WaDevice = isPutriGroup ? WHACENTER_DEVICE_ID_PUTRI : WHACENTER_DEVICE_ID;
    const target = isGroup ? { group: log.recipient } : { number: log.recipient };

    // Versi sederhana dari sendRequest, tanpa membuat log baru.
    try {
        const response = await sendWhatsapp(target, log.message, deviceId);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            await updateWhatsappLog(log.id, 'failed', errorData.info || 'Gagal mengirim ulang.');
        } else {
            await updateWhatsappLog(log.id, 'sent');
        }
    } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        await updateWhatsappLog(log.id, 'failed', errorMsg);
    }
};

export const handleCheckoutNotification = async (permit: Permit) => {
    // Skip WA notification for activity appreciation permits to prevent spam/banning
    if (permit.relatedKegiatanId) {
        console.log(`[WhatsApp] Skip notifikasi checkout untuk izin apresiasi kegiatan (Kegiatan ID: ${permit.relatedKegiatanId})`);
        return;
    }

    const isPutri = permit.santri[0]?.jenisKelamin === 'Perempuan';

    if (permit.permitType === PermitType.PULANG) {
        const message = formatIzinPulangCheckout(permit);
        const santri = permit.santri[0];

        // Kirim ke wali
        if (santri.noHp) {
            const deviceId = isPutri ? WHACENTER_DEVICE_ID_PUTRI : WHACENTER_DEVICE_ID;
            await sendToNumber(santri.noHp, message, permit, deviceId);
        }

        // Kirim ke grup yang sesuai
        let groupName = getJenjangFromClass(santri.kelas) === 'SMP' ? GROUP_NAME_SMP : GROUP_NAME_SMA;
        let deviceId: WaDevice = WHACENTER_DEVICE_ID;

        if (isPutri) {
            groupName = getJenjangFromClass(santri.kelas) === 'SMP' ? GROUP_NAME_PUTRI_SMP : GROUP_NAME_PUTRI_SMA;
            deviceId = WHACENTER_DEVICE_ID_PUTRI;
        }

        await sendToGroup(groupName, message, permit, deviceId);

    } else if (permit.permitType === PermitType.IZIN_TERBIMBING) {
        const message = formatIzinTerbimbing(permit, 'checkout');

        if (isPutri) {
            // Kirim ke kedua grup putri
            await sendToGroup(GROUP_NAME_PUTRI_SMP, message, permit, WHACENTER_DEVICE_ID_PUTRI);
            await sendToGroup(GROUP_NAME_PUTRI_SMA, message, permit, WHACENTER_DEVICE_ID_PUTRI);
        } else {
            // Kirim ke kedua grup putra
            await sendToGroup(GROUP_NAME_SMP, message, permit, WHACENTER_DEVICE_ID);
            await sendToGroup(GROUP_NAME_SMA, message, permit, WHACENTER_DEVICE_ID);
        }
    }
};

export const handleCheckinNotification = async (permit: Permit) => {
    // Skip WA notification for activity appreciation permits to prevent spam/banning
    if (permit.relatedKegiatanId) {
        console.log(`[WhatsApp] Skip notifikasi checkin untuk izin apresiasi kegiatan (Kegiatan ID: ${permit.relatedKegiatanId})`);
        return;
    }

    const isPutri = permit.santri[0]?.jenisKelamin === 'Perempuan';

    if (permit.permitType === PermitType.PULANG) {
        const message = formatIzinPulangCheckin(permit);
        const santri = permit.santri[0];

        // Kirim ke wali
        if (santri.noHp) {
            const deviceId = isPutri ? WHACENTER_DEVICE_ID_PUTRI : WHACENTER_DEVICE_ID;
            await sendToNumber(santri.noHp, message, permit, deviceId);
        }

        // Kirim ke grup yang sesuai
        let groupName = getJenjangFromClass(santri.kelas) === 'SMP' ? GROUP_NAME_SMP : GROUP_NAME_SMA;
        let deviceId: WaDevice = WHACENTER_DEVICE_ID;

        if (isPutri) {
            groupName = getJenjangFromClass(santri.kelas) === 'SMP' ? GROUP_NAME_PUTRI_SMP : GROUP_NAME_PUTRI_SMA;
            deviceId = WHACENTER_DEVICE_ID_PUTRI;
        }

        await sendToGroup(groupName, message, permit, deviceId);

    } else if (permit.permitType === PermitType.IZIN_TERBIMBING) {
        const message = formatIzinTerbimbing(permit, 'checkin');

        if (isPutri) {
            // Kirim ke kedua grup putri
            await sendToGroup(GROUP_NAME_PUTRI_SMP, message, permit, WHACENTER_DEVICE_ID_PUTRI);
            await sendToGroup(GROUP_NAME_PUTRI_SMA, message, permit, WHACENTER_DEVICE_ID_PUTRI);
        } else {
            // Kirim ke kedua grup putra
            await sendToGroup(GROUP_NAME_SMP, message, permit, WHACENTER_DEVICE_ID);
            await sendToGroup(GROUP_NAME_SMA, message, permit, WHACENTER_DEVICE_ID);
        }
    }
};


// --- Izin Staf Notifications ---

const formatIzinStafPengajuan = (izin: IzinStafRecord): string => {
    let waktuInfo = '';
    if (izin.jenis_izin === 'Harian') {
        waktuInfo = `Dari Tanggal : ${formatDateOnly(izin.tanggal_mulai)}
Sampai Tanggal : ${formatDateOnly(izin.tanggal_selesai || izin.tanggal_mulai)}`;
    } else if (izin.jenis_izin === 'Perjam') {
        let durasi = '';
        if (izin.jam_mulai && izin.jam_selesai) {
             const startParts = izin.jam_mulai.split(':');
             const endParts = izin.jam_selesai.split(':');
             if (startParts.length >= 2 && endParts.length >= 2) {
                 const start = new Date(2000, 0, 1, parseInt(startParts[0]), parseInt(startParts[1]));
                 const end = new Date(2000, 0, 1, parseInt(endParts[0]), parseInt(endParts[1]));
                 let diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                 if (diff < 0) diff += 24;
                 durasi = `${Math.round(diff)} jam (${izin.jam_selesai.substring(0, 5)})`;
             }
        }
        waktuInfo = `Tanggal : ${formatDateOnly(izin.tanggal_mulai)}
Dari Jam : ${izin.jam_mulai?.substring(0, 5) || '-'}
Durasi : ${durasi}`;
    } else if (izin.jenis_izin === 'Pulang Cepat') {
        waktuInfo = `Tanggal : ${formatDateOnly(izin.tanggal_mulai)}
Jam pulang : ${izin.jam_mulai?.substring(0, 5) || '-'}`;
    } else if (izin.jenis_izin === 'Terlambat') {
        waktuInfo = `Tanggal : ${formatDateOnly(izin.tanggal_mulai)}
Jam datang : ${izin.jam_mulai?.substring(0, 5) || '-'}`;
    }

    return `*Pemberitahuan Pengajuan Izin*

Assalamu'alaikum,
Dengan ini diberitahukan bahwa terdapat pengajuan izin staf/guru:

- *Nama:* ${izin.nama_pegawai}
- *Jenis Izin:* ${izin.jenis_izin}
- *Alasan Izin:* ${izin.alasan}
- *Waktu:*
${waktuInfo}
- *Status:* ${izin.status}

Mohon untuk mengecek pengajuan perizinan ini di Portal MIAS untuk melihat lebih detail dan memproses persetujuan (ACC/Tolak). Terima kasih.`;
};

const formatIzinStafDiproses = (izin: IzinStafRecord): string => {
    let waktuInfo = '';
    if (izin.jenis_izin === 'Harian') {
        waktuInfo = `Dari Tanggal : ${formatDateOnly(izin.tanggal_mulai)}
Sampai Tanggal : ${formatDateOnly(izin.tanggal_selesai || izin.tanggal_mulai)}`;
    } else if (izin.jenis_izin === 'Perjam') {
         let durasi = '';
         if (izin.jam_mulai && izin.jam_selesai) {
             const startParts = izin.jam_mulai.split(':');
             const endParts = izin.jam_selesai.split(':');
             if (startParts.length >= 2 && endParts.length >= 2) {
                 const start = new Date(2000, 0, 1, parseInt(startParts[0]), parseInt(startParts[1]));
                 const end = new Date(2000, 0, 1, parseInt(endParts[0]), parseInt(endParts[1]));
                 let diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                 if (diff < 0) diff += 24;
                 durasi = `${Math.round(diff)} jam (${izin.jam_selesai.substring(0, 5)})`;
             }
         }
         waktuInfo = `Tanggal : ${formatDateOnly(izin.tanggal_mulai)}
Dari Jam : ${izin.jam_mulai?.substring(0, 5) || '-'}
Durasi : ${durasi}`;
    } else if (izin.jenis_izin === 'Pulang Cepat') {
        waktuInfo = `Tanggal : ${formatDateOnly(izin.tanggal_mulai)}
Jam pulang : ${izin.jam_mulai?.substring(0, 5) || '-'}`;
    } else if (izin.jenis_izin === 'Terlambat') {
        waktuInfo = `Tanggal : ${formatDateOnly(izin.tanggal_mulai)}
Jam datang : ${izin.jam_mulai?.substring(0, 5) || '-'}`;
    }

    const statusText = izin.status === 'Disetujui' ? 'DITERIMA' : 'DITOLAK';
    const catatanInfo = izin.catatan_persetujuan ? `
- *Catatan Persetujuan:* ${izin.catatan_persetujuan}` : '';

    return `*Pemberitahuan Hasil Pengajuan Izin*

Assalamu'alaikum ${izin.nama_pegawai},
Pengajuan izin Anda telah diproses dengan status: *${statusText}*

Detail Izin:
- *Jenis Izin:* ${izin.jenis_izin}
- *Alasan Izin:* ${izin.alasan}
- *Waktu:*
${waktuInfo}
- *Diproses Oleh:* ${izin.nama_penerima || 'Petugas Izin'}${catatanInfo}

Untuk melihat detail selengkapnya silakan cek di Portal MIAS. Terima kasih.`;
};

export const handleIzinStafPengajuanNotification = async (izin: IzinStafRecord, approverNoHp: string) => {
    if (!approverNoHp) return;
    const message = formatIzinStafPengajuan(izin);
    const logData = {
        permitId: izin.id,
        santriName: izin.nama_pegawai, // Menggunakan nama_pegawai untuk field santriName di log
        recipient: approverNoHp,
        message: message,
    };
    await sendRequest({ number: approverNoHp }, message, logData);
};

export const handleIzinStafDiprosesNotification = async (izin: IzinStafRecord, pengajuNoHp: string) => {
    if (!pengajuNoHp) return;
    const message = formatIzinStafDiproses(izin);
    const logData = {
        permitId: izin.id,
        santriName: izin.nama_pegawai,
        recipient: pengajuNoHp,
        message: message,
    };
    await sendRequest({ number: pengajuNoHp }, message, logData);
};
